import type { ApiClient } from "@/api/client";
import { ApiError } from "@/api/errors";

/**
 * 案件（process instance）を取る。
 *
 * `for-me` は**その案件に関わっている人**（起票者・担当者）しか通さない。全案件
 * モニタから自分が関与していない案件を開くと 400
 * （`process_instance_cannot_be_found`「associated with you」）になるので、
 * 管理者経路に落として取り直す。落とさないと**一覧には出ているのに詳細が開けない**。
 *
 * 先に `for-me` を叩くのは、関与者にとってはこちらが常に通り、管理権限を要求しないため。
 */
export async function fetchProcessInstance<T>(
  client: ApiClient,
  modifiedProcessModelId: string,
  caseId: number,
): Promise<T> {
  try {
    return await client.get<T>(
      `/process-instances/for-me/${modifiedProcessModelId}/${caseId}`,
    );
  } catch (error) {
    // 落とすのは「関与していないので for-me では取れない」ときだけ。
    // 通信断・401・403・5xx を落とすと、原因を隠したまま 2 回叩くことになる
    // （401 なら再ログイン処理が二重に動く）。
    if (!isNotAssociatedWithUser(error)) throw error;

    return await client.get<T>(
      `/process-instances/${modifiedProcessModelId}/${caseId}`,
    );
  }
}

/** for-me の「あなたに紐づく案件が見つからない」応答か。 */
function isNotAssociatedWithUser(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    error.errorCode === NOT_ASSOCIATED_ERROR_CODE
  );
}

/** backend の error_code（`process_api_blueprint.py` が返す安定値）。 */
const NOT_ASSOCIATED_ERROR_CODE = "process_instance_cannot_be_found";
